import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

const WP_ADMIN_PAGE_PARAM = /\?page=allcoach/g;

export function stripWpParam(value: string): string {
	return value.replace(WP_ADMIN_PAGE_PARAM, '').trim();
}

export function parseString(value: unknown): string | undefined {
	if (typeof value !== 'string') return undefined;
	return stripWpParam(value) || undefined;
}

export function parsePositiveInt(value: unknown): number | undefined {
	if (typeof value === 'number') {
		return Number.isInteger(value) && value > 0 ? value : undefined;
	}
	const str = parseString(value);
	if (!str) return undefined;
	const num = Number(str);
	return Number.isInteger(num) && num > 0 ? num : undefined;
}

export function parseDate(value: unknown): string | undefined {
	const str = parseString(value);
	if (!str) return undefined;
	const date = new Date(str);
	return isNaN(date.getTime()) ? undefined : str;
}

export function parseEnum<T extends string | number>(
	value: unknown,
	allowed: readonly T[],
): T | undefined {
	const parsed =
		typeof allowed[0] === 'number'
			? parsePositiveInt(value)
			: parseString(value);

	return allowed.includes(parsed as T) ? (parsed as T) : undefined;
}

export type PerPage = 10 | 25 | 50 | 100;

export interface BaseListSearchParams {
	page: number;
	per_page: PerPage;
	search: string | undefined;
	from_date: string | undefined;
	to_date: string | undefined;
}

const DEFAULTS = {
	page: 1,
	per_page: 10 as PerPage,
} as const;

const ALLOWED_PER_PAGE = [
	10, 25, 50, 100,
] as const satisfies readonly PerPage[];

export function sanitizeSearchParams<
	TExtra extends Record<string, unknown> = Record<never, never>,
>(
	raw: Record<string, unknown>,
	extend?: (raw: Record<string, unknown>) => TExtra,
): BaseListSearchParams & TExtra {
	const base: BaseListSearchParams = {
		page: parsePositiveInt(raw.page) ?? DEFAULTS.page,
		per_page: parseEnum(raw.per_page, ALLOWED_PER_PAGE) ?? DEFAULTS.per_page,
		search: parseString(raw.search),
		from_date: parseDate(raw.from_date),
		to_date: parseDate(raw.to_date),
	};

	return {
		...base,
		...(extend?.(raw) ?? ({} as TExtra)),
	};
}
